import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Card, CardBody, Spinner } from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import AccountExpenseAddEdit from "pages/AccountPages/AccountExpense/AccountExpenseAddEdit"
import AccountVendorBillAddEdit from "pages/AccountPages/AccountVendorBill/AccountVendorBillAddEdit"
import AccountVendorPaymentAddEdit from "pages/AccountPages/AccountVendorPayment/AccountVendorPaymentAddEdit"
import AccountLoanAddEdit from "pages/AccountPages/AccountLoan/AccountLoanAddEdit"
import AccountLoanRepaymentAddEdit from "pages/AccountPages/AccountLoanRepayment/AccountLoanRepaymentAddEdit"
import AccountFundTransferAddEdit from "pages/AccountPages/AccountFundTransfer/AccountFundTransferAddEdit"
import WorkspaceTabNav from "./components/WorkspaceTabNav"
import { getVisibleWorkspaceTabs } from "./workspaceConfig"
import OverviewTab from "./tabs/OverviewTab"
import PayablesTab from "./tabs/PayablesTab"
import ReceivablesTab from "./tabs/ReceivablesTab"
import LoansTab from "./tabs/LoansTab"
import CashBankTab from "./tabs/CashBankTab"
import LedgerTab from "./tabs/LedgerTab"
import SetupTab from "./tabs/SetupTab"
import AdminTab from "./tabs/AdminTab"

const AccountWorkspace = () => {
  document.title = "Finance Workspace"

  const { hasAnyPermission, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [actionModal, setActionModal] = useState({ type: null, defaults: null })

  const tabs = useMemo(() => getVisibleWorkspaceTabs(hasAnyPermission), [hasAnyPermission])
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const requestedTab = searchParams.get("tab")
  const firstTab = tabs[0]?.id || null
  const activeTab = tabs.some(tab => tab.id === requestedTab) ? requestedTab : firstTab

  const permissionFlags = useMemo(() => ({
    canViewAnyFinance: hasAnyPermission([
      ACCESS.ACCOUNT_MANAGEMENT.VIEW,
      ACCESS.ACCOUNT.VIEW_LIST,
      ACCESS.ACCOUNT_VENDOR_BILL.VIEW_LIST,
      ACCESS.ACCOUNT_VENDOR_PAYMENT.VIEW_LIST,
      ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST,
      ACCESS.ACCOUNT_LOAN.VIEW_LIST,
      ACCESS.ACCOUNT_LOAN_REPAYMENT.VIEW_LIST,
      ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST,
      ACCESS.ACCOUNT_EXPENSE.VIEW_LIST,
      ACCESS.ACCOUNT_FUND_TRANSFER.VIEW_LIST,
    ]),
    canViewAccounts: hasAnyPermission([ACCESS.ACCOUNT.VIEW_LIST]),
    canViewVendorBills: hasAnyPermission([ACCESS.ACCOUNT_VENDOR_BILL.VIEW_LIST]),
    canViewVendorPayments: hasAnyPermission([ACCESS.ACCOUNT_VENDOR_PAYMENT.VIEW_LIST]),
    canViewCustomerPayments: hasAnyPermission([ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST]),
    canViewLoans: hasAnyPermission([ACCESS.ACCOUNT_LOAN.VIEW_LIST]),
    canViewLoanRepayments: hasAnyPermission([ACCESS.ACCOUNT_LOAN_REPAYMENT.VIEW_LIST]),
    canViewExpenses: hasAnyPermission([ACCESS.ACCOUNT_EXPENSE.VIEW_LIST]),
    canViewFundTransfers: hasAnyPermission([ACCESS.ACCOUNT_FUND_TRANSFER.VIEW_LIST]),
    canViewTransactions: hasAnyPermission([ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST]),
    canViewVehiclePurchases: hasAnyPermission([ACCESS.ACCOUNT_VEHICLE_PURCHASE.VIEW_LIST]),
    canViewImporterPayments: hasAnyPermission([ACCESS.ACCOUNT_IMPORTER_PAYMENT.VIEW_LIST]),
    canAddExpense: hasAnyPermission([ACCESS.ACCOUNT_EXPENSE.ADD]),
    canAddVendorBill: hasAnyPermission([ACCESS.ACCOUNT_VENDOR_BILL.ADD]),
    canAddVendorPayment: hasAnyPermission([ACCESS.ACCOUNT_VENDOR_PAYMENT.ADD]),
    canAddCustomerPayment: hasAnyPermission([ACCESS.ACCOUNT_CUSTOMER_PAYMENT.ADD]),
    canAddLoan: hasAnyPermission([ACCESS.ACCOUNT_LOAN.ADD]),
    canAddRepayment: hasAnyPermission([ACCESS.ACCOUNT_LOAN_REPAYMENT.ADD]),
    canAddFundTransfer: hasAnyPermission([ACCESS.ACCOUNT_FUND_TRANSFER.ADD]),
  }), [hasAnyPermission])

  useEffect(() => {
    if (isLoading || !tabs.length) return
    if (!requestedTab || !tabs.some(tab => tab.id === requestedTab)) {
      navigate(`/account-workspace?tab=${tabs[0].id}`, { replace: true })
    }
  }, [isLoading, requestedTab, tabs, navigate])

  const setTab = tabId => navigate(`/account-workspace?tab=${tabId}`)
  const openQuickAction = (type, defaults = null) => setActionModal({ type, defaults })
  const closeQuickAction = () => setActionModal({ type: null, defaults: null })

  const renderedTab = useMemo(() => {
    switch (activeTab) {
      case "overview":
        return <OverviewTab permissionFlags={permissionFlags} onQuickAction={openQuickAction} onTabChange={setTab} />
      case "payables":
        return <PayablesTab permissionFlags={permissionFlags} />
      case "receivables":
        return <ReceivablesTab permissionFlags={permissionFlags} />
      case "loans":
        return <LoansTab permissionFlags={permissionFlags} />
      case "cash-bank":
        return <CashBankTab permissionFlags={permissionFlags} />
      case "ledger":
        return <LedgerTab />
      case "setup":
        return <SetupTab />
      case "admin":
        return <AdminTab />
      default:
        return null
    }
  }, [activeTab, permissionFlags])

  if (isLoading) {
    return (
      <PageWrapper title="Finance Workspace" breadcrumbItem="Finance Workspace">
        <div className="text-center py-5"><Spinner color="primary" /></div>
      </PageWrapper>
    )
  }

  if (!tabs.length) {
    return (
      <PageWrapper title="Finance Workspace" breadcrumbItem="Finance Workspace">
        <Card className="border-0 shadow-sm">
          <CardBody className="py-5 text-center">
            <h5 className="mb-2">No finance modules available</h5>
            <p className="text-muted mb-0">This user does not currently have access to any finance workspace sections.</p>
          </CardBody>
        </Card>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper title="Finance Workspace" breadcrumbItem="Finance Workspace">
      <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start mb-4">
        <div>
          <h4 className="mb-1">Finance Workspace</h4>
          <p className="text-muted mb-0">Manage payables, receivables, loans, cash movement, and setup from one role-aware place.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {permissionFlags.canAddExpense && <Button color="light" className="border" onClick={() => openQuickAction("expense")}><i className="mdi mdi-receipt-text-plus-outline me-1" />Expense</Button>}
          {permissionFlags.canAddVendorPayment && <Button color="light" className="border" onClick={() => openQuickAction("vendor-payment")}><i className="mdi mdi-cash-plus me-1" />Vendor Payment</Button>}
          {permissionFlags.canAddLoan && <Button color="light" className="border" onClick={() => openQuickAction("loan")}><i className="mdi mdi-cash me-1" />Loan</Button>}
          {permissionFlags.canAddFundTransfer && <Button color="primary" onClick={() => openQuickAction("fund-transfer")}><i className="mdi mdi-bank-transfer-in me-1" />Fund Transfer</Button>}
        </div>
      </div>

      <div className="mb-4">
        <WorkspaceTabNav tabs={tabs} activeTab={activeTab} onChange={setTab} />
      </div>

      {renderedTab}

      <AccountExpenseAddEdit modal={actionModal.type === "expense"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
      <AccountVendorBillAddEdit modal={actionModal.type === "vendor-bill"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
      <AccountVendorPaymentAddEdit modal={actionModal.type === "vendor-payment"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
      <AccountLoanAddEdit modal={actionModal.type === "loan"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
      <AccountLoanRepaymentAddEdit modal={actionModal.type === "loan-repayment"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
      <AccountFundTransferAddEdit modal={actionModal.type === "fund-transfer"} isEdit={false} editedData={actionModal.defaults} toggleModal={closeQuickAction} />
    </PageWrapper>
  )
}

export default AccountWorkspace
